import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/api";
import "./ForgetPassword.css";
import { 
  FiMail, 
  FiPhone, 
  FiUser, 
  FiArrowLeft, 
  FiCheckCircle, 
  FiAlertCircle,
  FiEye,
  FiEyeOff,
  FiLock,
  FiKey,
  FiInfo
} from "react-icons/fi";

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: Enter identifier, 2: Verify OTP, 3: Reset password
  const [formData, setFormData] = useState({
    identifier: "",
    otp: "",
    new_password: "",
    confirm_password: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [showPassword, setShowPassword] = useState({
    new_password: false,
    confirm_password: false
  });

  const navigate = useNavigate();

  // Detect identifier type and send method automatically
  const detectIdentifierType = (value) => {
    if (!value) return { type: "email", method: "email" };
    
    if (value.includes("@")) {
      return { type: "email", method: "email" };
    } else if (/^\d+$/.test(value) && value.length >= 10) {
      return { type: "phone", method: "sms" };
    } else {
      return { type: "membership_id", method: "email" };
    }
  };

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, identifier: value });
    setError("");
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData({ ...formData, otp: value });
    setError("");
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError("");
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Start countdown timer for OTP resend
  const startCountdown = () => {
    setCountdown(60);
    setCanResend(false);
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Validate identifier
  const validateIdentifier = () => {
    const { identifier } = formData;
    
    if (!identifier.trim()) {
      setError("Please enter your email, phone number, or membership ID");
      return false;
    }

    const { type } = detectIdentifierType(identifier);
    
    if (type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(identifier)) {
        setError("Please enter a valid email address");
        return false;
      }
    } else if (type === "phone") {
      if (identifier.length < 10) {
        setError("Please enter a valid phone number (at least 10 digits)");
        return false;
      }
    } else if (type === "membership_id") {
      if (identifier.length < 3) {
        setError("Please enter a valid membership ID");
        return false;
      }
    }

    return true;
  };

  // Validate OTP
  const validateOTP = () => {
    const { otp } = formData;
    
    if (!otp.trim()) {
      setError("Please enter the 6-digit OTP");
      return false;
    }

    if (otp.length !== 6) {
      setError("OTP must be 6 digits");
      return false;
    }

    return true;
  };

  // Validate passwords
  const validatePasswords = () => {
    const { new_password, confirm_password } = formData;
    
    if (!new_password.trim()) {
      setError("Please enter a new password");
      return false;
    }

    if (new_password.length < 8) {
      setError("Password must be at least 8 characters long");
      return false;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(new_password)) {
      setError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
      return false;
    }

    if (!confirm_password.trim()) {
      setError("Please confirm your new password");
      return false;
    }

    if (new_password !== confirm_password) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  // Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateIdentifier()) return;

    setLoading(true);

    try {
      const { identifier } = formData;
      const { method } = detectIdentifierType(identifier);

      const payload = {
        identifier: identifier,
        send_via: method
      };

      console.log("📤 Sending password reset request:", payload);
      
      const response = await api.post("/password/forgot", payload);

      console.log("✅ Password reset request response:", response.data);

      if (response.data.status === "success" || response.data.success) {
        const methodText = method === "email" ? "email" : "phone";
        setSuccess(`Verification code sent to your ${methodText}!`);
        setStep(2);
        startCountdown();
      } else {
        setError(response.data.message || "Failed to send verification code");
      }
    } catch (error) {
      console.error("❌ Password reset request error:", error);
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.errors && typeof data.errors === 'object') {
          const errorMessages = Object.values(data.errors).flat();
          setError(errorMessages.join(', '));
        } else if (data.message) {
          setError(data.message);
        } else {
          setError("Failed to send verification code. Please try again.");
        }
      } else if (error.code === "ERR_NETWORK") {
        setError("Cannot connect to server. Please check your internet connection.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateOTP()) return;

    setLoading(true);

    try {
      const payload = {
        identifier: formData.identifier,
        otp: formData.otp
      };

      console.log("📤 Verifying OTP:", payload);
      
      const response = await api.post("/password/verify-otp", payload);

      console.log("✅ OTP verification response:", response.data);

      if (response.data.status === "success" || response.data.success) {
        setSuccess("Code verified successfully!");
        setStep(3);
      } else {
        setError(response.data.message || "Invalid verification code");
      }
    } catch (error) {
      console.error("❌ OTP verification error:", error);
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.errors && typeof data.errors === 'object') {
          const errorMessages = Object.values(data.errors).flat();
          setError(errorMessages.join(', '));
        } else if (data.message) {
          setError(data.message);
        } else {
          setError("Failed to verify code. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validatePasswords()) return;

    setLoading(true);

    try {
      const payload = {
        identifier: formData.identifier,
        otp: formData.otp,
        password: formData.new_password,
        password_confirmation: formData.confirm_password
      };

      console.log("📤 Resetting password:", payload);
      
      const response = await api.post("/password/reset", payload);

      console.log("✅ Password reset response:", response.data);

      if (response.data.status === "success" || response.data.success) {
        setSuccess("Password reset successfully! Redirecting to login...");
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login", { 
            state: { 
              message: "password_reset_success",
              identifier: formData.identifier 
            } 
          });
        }, 2000);
      } else {
        setError(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("❌ Password reset error:", error);
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.errors && typeof data.errors === 'object') {
          const errorMessages = Object.values(data.errors).flat();
          setError(errorMessages.join(', '));
        } else if (data.message) {
          setError(data.message);
        } else {
          setError("Failed to reset password. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (!canResend) return;

    setError("");
    setSuccess("");

    setLoading(true);

    try {
      const { identifier } = formData;
      const { method } = detectIdentifierType(identifier);

      const payload = {
        identifier: identifier,
        send_via: method
      };

      console.log("📤 Resending OTP:", payload);
      
      const response = await api.post("/password/resend-otp", payload);

      console.log("✅ Resend OTP response:", response.data);

      if (response.data.status === "success" || response.data.success) {
        const methodText = method === "email" ? "email" : "phone";
        setSuccess(`New code sent to your ${methodText}!`);
        startCountdown();
      } else {
        setError(response.data.message || "Failed to resend code");
      }
    } catch (error) {
      console.error("❌ Resend OTP error:", error);
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.message) {
          setError(data.message);
        } else {
          setError("Failed to resend code. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getIdentifierIcon = () => {
    const { type } = detectIdentifierType(formData.identifier);
    switch (type) {
      case "email": return <FiMail />;
      case "phone": return <FiPhone />;
      case "membership_id": return <FiUser />;
      default: return <FiMail />;
    }
  };

  const getIdentifierPlaceholder = () => {
    if (!formData.identifier) {
      return "Enter email or phone number";
    }
    const { type } = detectIdentifierType(formData.identifier);
    switch (type) {
      case "email": return "example@domain.com";
      case "phone": return "08012345678";
      case "membership_id": return "MEM12345";
      default: return "Enter your identifier";
    }
  };

  const getDeliveryMethodText = () => {
    const { identifier } = formData;
    if (!identifier) return "";
    
    const { method } = detectIdentifierType(identifier);
    return method === "email" ? "email" : "phone";
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        {/* Back to Login */}
        <div className="back-to-login">
          <Link to="/login" className="back-link">
            <FiArrowLeft /> Back to Login
          </Link>
        </div>

        <div className="forgot-password-header">
          <h2>Reset Your Password</h2>
          <p className="subtitl">
            {step === 1 && "Enter your account details to receive a verification code"}
            {step === 2 && `Enter the 6-digit code sent to your ${getDeliveryMethodText()}`}
            {step === 3 && "Create a new password for your account"}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step ${step >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <span className="step-label">Find Account</span>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <span className="step-label">Verify Code</span>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step >= 3 ? "active" : ""}`}>
            <div className="step-number">3</div>
            <span className="step-label">New Password</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <FiAlertCircle className="error-icon" />
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="success-message">
            <FiCheckCircle className="success-icon" />
            <span>{success}</span>
          </div>
        )}

        {/* Step 1: Enter Identifier */}
        {step === 1 && (
          <form onSubmit={handleRequestOTP} className="forgot-password-form">
            <div className="form-group">
              <label>Email, Phone</label>
              <div className="input-with-icon">
                {/* <span className="input-icon">{getIdentifierIcon()}</span> */}
                <input
                  type="text"
                  value={formData.identifier}
                  onChange={handleIdentifierChange}
                  placeholder={getIdentifierPlaceholder()}
                  className="form-input"
                  autoFocus
                />
              </div>
              {formData.identifier && (
                <p className="helper-text">
                  <FiInfo />
                  Code will be sent via <strong>{getDeliveryMethodText()}</strong>
                </p>
              )}
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading || !formData.identifier.trim()}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Sending Code...
                </>
              ) : (
                "Send Verification Code"
              )}
            </button>
          </form>
        )}

        {/* Step 2: Verify OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOTP} className="forgot-password-form">
            <div className="form-group">
              <label>6-Digit Verification Code</label>
              <div className="otp-input-container">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={formData.otp}
                  onChange={handleOtpChange}
                  placeholder="000000"
                  className="otp-input"
                  autoFocus
                />
              </div>
              <p className="helper-text">
                <FiKey /> Enter the 6-digit code sent to your {getDeliveryMethodText()}
              </p>
            </div>

            <div className="resend-section">
              <p>Didn't receive the code?</p>
              <button
                type="button"
                className={`resend-btn ${!canResend ? "disabled" : ""}`}
                onClick={handleResendOTP}
                disabled={!canResend || loading}
              >
                {canResend ? "Resend Code" : `Resend in ${countdown}s`}
              </button>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="back-btn"
                onClick={() => setStep(1)}
                disabled={loading}
              >
                Back
              </button>
              <button 
                type="submit" 
                className="verify-btn"
                disabled={loading || formData.otp.length !== 6}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Reset Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="forgot-password-form">
            <div className="form-group">
              <label>New Password</label>
              <div className="password-input-wrapper">
                <span className="input-icon"><FiLock /></span>
                <input
                  type={showPassword.new_password ? "text" : "password"}
                  name="new_password"
                  value={formData.new_password}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  className="form-input with-left-icon"
                  autoFocus
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility("new_password")}
                >
                  {showPassword.new_password ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <div className="password-strength">
                <div className={`strength-bar ${formData.new_password.length >= 8 ? 'active' : ''}`}></div>
                <div className={`strength-bar ${/(?=.*[a-z])/.test(formData.new_password) ? 'active' : ''}`}></div>
                <div className={`strength-bar ${/(?=.*[A-Z])/.test(formData.new_password) ? 'active' : ''}`}></div>
                <div className={`strength-bar ${/(?=.*\d)/.test(formData.new_password) ? 'active' : ''}`}></div>
              </div>
              <p className="helper-text">
                Must be 8+ characters with uppercase, lowercase, and number
              </p>
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <div className="password-input-wrapper">
                <span className="input-icon"><FiLock /></span>
                <input
                  type={showPassword.confirm_password ? "text" : "password"}
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  className="form-input with-left-icon"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility("confirm_password")}
                >
                  {showPassword.confirm_password ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {formData.confirm_password && formData.new_password === formData.confirm_password && (
                <p className="success-text">
                  <FiCheckCircle /> Passwords match
                </p>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="back-btn"
                onClick={() => setStep(2)}
                disabled={loading}
              >
                Back
              </button>
              <button 
                type="submit" 
                className="reset-btn"
                disabled={loading || !formData.new_password || !formData.confirm_password}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>
            </div>
          </form>
        )}

        {/* Additional Help */}
        <div className="additional-help">
          <p>
            Need help? Contact support at{" "}
            <a href="mailto:support@tinubu-ambode.org">support@tinubu-ambode.org</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;